// eslint-disable-next-line no-restricted-imports
import { TransactionResponse } from '@ethersproject/providers'
import { t, Trans } from '@lingui/macro'
import { ethers } from 'ethers'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { ReactNode, useMemo } from 'react'

import { UselessNft__factory } from '../../../abis/types'
import { MINT_PRICE } from '../../../constants/misc'
import { useUselessNft } from '../../../hooks/useContract'
import { calculateGasMargin } from '../../../utils/calculateGasMargin'
import { swapErrorToUserReadableMessage } from '../../../utils/swapErrorToUserReadableMessage'
import { useMintCallArguments } from './useMintCallArguments'

export enum MintCallbackState {
  INVALID,
  LOADING,
  VALID,
}

interface UseMintCallbackReturns {
  state: MintCallbackState
  callback: null | (() => Promise<TransactionResponse>)
  error: ReactNode | null
}
interface UseMintCallbackArgs {
  amount: number | undefined
}

// returns a function that will execute a swap, if the parameters are all valid
// and the user has approved the slippage adjusted input amount for the trade
export function useMintCallback({ amount }: UseMintCallbackArgs): UseMintCallbackReturns {
  const { account, chainId, library } = useActiveWeb3React()

  const uselessNft = useUselessNft()
  const callback = useMintCallArguments(amount)

  return useMemo(() => {
    if (!library || !account || !chainId || !callback || !uselessNft || !amount) {
      return { state: MintCallbackState.INVALID, callback: null, error: <Trans>Missing dependencies</Trans> }
    }
    if (amount > 5 || amount < 0) {
      return { state: MintCallbackState.INVALID, callback: null, error: <Trans>Invalid amount to mint</Trans> }
    }
    return {
      state: MintCallbackState.VALID,
      callback: async function onMint(): Promise<TransactionResponse> {
        const tx = {
          from: account,
          to: uselessNft.address,
          data: UselessNft__factory.createInterface().encodeFunctionData('mint', [amount]),
          value: ethers.BigNumber.from(amount).mul(MINT_PRICE.toString()),
        }

        const { gasEstimate, error } = await library
          .estimateGas(tx)
          .then((gasEstimate) => ({ gasEstimate, error: undefined }))
          .catch((gasError) => {
            console.debug('Gas estimate failed, trying eth_call to extract error', tx)

            return library
              .call(tx)
              .then((result) => {
                console.debug('Unexpected successful call after failed estimate gas', tx, gasError, result)
                return {
                  gasEstimate: undefined,
                  error: <Trans>Unexpected issue with estimating the gas. Please try again.</Trans>,
                }
              })
              .catch((callError) => {
                console.debug('Call threw error', tx, callError)
                return { gasEstimate: undefined, error: swapErrorToUserReadableMessage(callError) }
              })
          })

        if (!gasEstimate && error) {
          throw new Error('Unexpected error. Could not estimate gas for the mint')
        }

        return library
          .getSigner()
          .sendTransaction({
            from: account,
            to: tx.to,
            data: tx.data,
            value: tx.value,
            ...(gasEstimate ? { gasLimit: calculateGasMargin(gasEstimate) } : {}),
          })
          .then((response) => {
            return response
          })
          .catch((error) => {
            // if the user rejected the tx, pass this along
            if (error?.code === 4001) {
              throw new Error(t`Transaction rejected.`)
            } else {
              // otherwise, the error was unexpected and we need to convey that
              console.error(`Mint failed`, error, tx.to, tx.data, tx.value)

              throw new Error(t`Mint failed: ${swapErrorToUserReadableMessage(error)}`)
            }
          })
      },
      error: null,
    }
  }, [account, amount, callback, chainId, library, uselessNft])
}
